import { Box, Link, Tooltip } from "@mui/material"
import { KeyboardEvent, useCallback, useEffect, useRef, useState } from "react"
import { intl } from "src/internationalization/intl"
import { Button, Chip, CircularProgress, Grid, Typography } from "src/lib"
import { TextInput } from "src/lib/components/basics/TextInput"
import { getMarketRankingDisplayName } from "src/parts/CommitmentsSection/useRankingAssets"
import {
    buildRankingPromptSnippet,
    formatPropertyTypeLabel,
    formatValueHintSummary,
    isEnumHint,
    getOrderingPropertiesForMarket,
    type OrderingPropertyMeta,
    type NumericFilterOp,
    type OrderingPropertiesForMarketResponse,
    type RankingPromptInsertOptions,
} from "src/services/api/sorting"

type NumericFilterDraft = {
    propertyName: string
    op: NumericFilterOp
    value: string
}

type RankingPropertiesPickerProps = {
    marketKey: string
    marketLabel?: string
    /** Called when user clicks a property / sample to build a prompt */
    onInsertPrompt: (snippet: string) => void
    /** Compact row under context card; false = full modal body */
    variant?: "inline" | "modal"
    onOpenModal?: () => void
}

const propertiesCache = new Map<string, OrderingPropertiesForMarketResponse>()

const RankingPropertiesPicker = ({
    marketKey,
    marketLabel,
    onInsertPrompt,
    variant = "inline",
    onOpenModal,
}: RankingPropertiesPickerProps) => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [data, setData] = useState<OrderingPropertiesForMarketResponse | null>(null)
    const [filterDraft, setFilterDraft] = useState<NumericFilterDraft | null>(null)
    const [filterError, setFilterError] = useState<string | null>(null)
    const mountedRef = useRef(true)

    const toggleFilterDraft = (propertyName: string, op: NumericFilterOp) => {
        setFilterError(null)
        setFilterDraft((prev) =>
            prev?.propertyName === propertyName && prev.op === op
                ? null
                : {
                      propertyName,
                      op,
                      value: prev?.propertyName === propertyName ? prev.value : "",
                  }
        )
    }

    const applyNumericFilter = (property: OrderingPropertyMeta) => {
        if (!filterDraft || filterDraft.propertyName !== property.name) return
        const val = filterDraft.value.trim().replace(",", ".")
        if (!val || !Number.isFinite(Number(val))) {
            setFilterError(intl("rankingPropertyFilterInvalidValue"))
            return
        }
        insert(property, {
            mode: "filter",
            filterOp: filterDraft.op,
            filterValue: val,
        })
        setFilterDraft(null)
        setFilterError(null)
    }

    const handleFilterKeyDown = (
        e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
        property: OrderingPropertyMeta
    ) => {
        if (e.key === "Enter") {
            e.preventDefault()
            applyNumericFilter(property)
        }
        if (e.key === "Escape") {
            setFilterDraft(null)
            setFilterError(null)
        }
    }

    const load = useCallback(async () => {
        const key = marketKey.trim()
        if (!key) return
        const cached = propertiesCache.get(key.toUpperCase())
        if (cached) {
            setData(cached)
            setError(null)
            return
        }
        setLoading(true)
        setError(null)
        try {
            const res = await getOrderingPropertiesForMarket(key)
            propertiesCache.set(key.toUpperCase(), res)
            if (mountedRef.current) setData(res)
        } catch {
            if (mountedRef.current) {
                setData(null)
                setError(intl("rankingPropertiesLoadError"))
            }
        } finally {
            if (mountedRef.current) setLoading(false)
        }
    }, [marketKey])

    useEffect(() => {
        mountedRef.current = true
        load()
        return () => {
            mountedRef.current = false
        }
    }, [load])

    const insert = (property: OrderingPropertyMeta, options?: RankingPromptInsertOptions) => {
        onInsertPrompt(buildRankingPromptSnippet(property, options))
    }

    const isModal = variant === "modal"
    const properties = data?.properties ?? []
    const title =
        marketLabel ??
        getMarketRankingDisplayName(data?.market || marketKey) ??
        marketKey

    if (loading) {
        return (
            <Grid container justifyContent="center" sx={{ py: isModal ? 2 : 1 }}>
                <CircularProgress color="primary" size={22} />
            </Grid>
        )
    }

    if (error) {
        return (
            <Typography variant="body2" sx={{ color: "error.main" }}>
                {error}
            </Typography>
        )
    }

    if (properties.length === 0) {
        return (
            <Typography variant="body2" color="secondary">
                {intl("rankingPropertiesEmpty")}
            </Typography>
        )
    }

    return (
        <Box>
            {isModal && (
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    {title}
                </Typography>
            )}
            {!isModal && (
                <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                    <Typography variant="caption" color="secondary">
                        {intl("rankingPropertiesClickHint")}
                    </Typography>
                    {onOpenModal && (
                        <Link
                            component="button"
                            type="button"
                            variant="caption"
                            underline="hover"
                            onClick={onOpenModal}
                            sx={{ cursor: "pointer", fontWeight: 600 }}
                        >
                            {intl("rankingPropertiesExpandModal")}
                        </Link>
                    )}
                </Grid>
            )}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    maxHeight: isModal ? "none" : 200,
                    overflowY: isModal ? "visible" : "auto",
                    pr: 0.5,
                }}
            >
                {properties.map((property) => {
                    const hintSummary = formatValueHintSummary(property)
                    const isNumeric =
                        property.type === "integer" ||
                        property.type === "decimal" ||
                        property.value_hint?.kind === "numeric_range"
                    const isBoolean =
                        property.type === "boolean" || property.value_hint?.kind === "boolean"
                    const samples = isEnumHint(property.value_hint)
                        ? property.value_hint.samples
                        : []
                    const hasOptions = isNumeric || isBoolean || samples.length > 0

                    return (
                        <Box
                            key={property.name}
                            sx={{
                                width: "100%",
                                pb: 0.75,
                                borderBottom: (t) =>
                                    `1px solid ${t.palette.divider}`,
                                "&:last-of-type": { borderBottom: "none", pb: 0 },
                            }}
                        >
                            <Tooltip
                                title={
                                    hintSummary
                                        ? `${formatPropertyTypeLabel(property.type)} · ${hintSummary}`
                                        : formatPropertyTypeLabel(property.type)
                                }
                                arrow
                            >
                                <span>
                                    <Chip
                                        label={property.name}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        onClick={() => insert(property)}
                                        sx={{ cursor: "pointer", fontWeight: 600 }}
                                    />
                                </span>
                            </Tooltip>
                            {hasOptions && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        alignItems: "center",
                                        gap: 0.5,
                                        mt: 0.5,
                                        pl: 0.25,
                                    }}
                                >
                                    {isNumeric && (
                                        <>
                                            <Chip
                                                label={intl("rankingPropertyHigher")}
                                                size="small"
                                                variant="filled"
                                                color="neutral"
                                                onClick={() => {
                                                    setFilterDraft(null)
                                                    insert(property, {
                                                        mode: "direction",
                                                        direction: "higher",
                                                    })
                                                }}
                                                sx={{ cursor: "pointer", fontSize: "0.7rem" }}
                                            />
                                            <Chip
                                                label={intl("rankingPropertyLower")}
                                                size="small"
                                                variant="filled"
                                                color="neutral"
                                                onClick={() => {
                                                    setFilterDraft(null)
                                                    insert(property, {
                                                        mode: "direction",
                                                        direction: "lower",
                                                    })
                                                }}
                                                sx={{ cursor: "pointer", fontSize: "0.7rem" }}
                                            />
                                            <Chip
                                                label={intl("rankingPropertyFilterGt")}
                                                size="small"
                                                variant={
                                                    filterDraft?.propertyName === property.name &&
                                                    filterDraft.op === "gt"
                                                        ? "filled"
                                                        : "outlined"
                                                }
                                                color="neutral"
                                                onClick={() => toggleFilterDraft(property.name, "gt")}
                                                sx={{ cursor: "pointer", fontSize: "0.7rem" }}
                                            />
                                            <Chip
                                                label={intl("rankingPropertyFilterLt")}
                                                size="small"
                                                variant={
                                                    filterDraft?.propertyName === property.name &&
                                                    filterDraft.op === "lt"
                                                        ? "filled"
                                                        : "outlined"
                                                }
                                                color="neutral"
                                                onClick={() => toggleFilterDraft(property.name, "lt")}
                                                sx={{ cursor: "pointer", fontSize: "0.7rem" }}
                                            />
                                        </>
                                    )}
                                    {isBoolean && (
                                        <>
                                            <Chip
                                                label={intl("rankingPropertyYes")}
                                                size="small"
                                                variant="filled"
                                                color="neutral"
                                                onClick={() =>
                                                    insert(property, { booleanValue: true })
                                                }
                                                sx={{ cursor: "pointer", fontSize: "0.7rem" }}
                                            />
                                            <Chip
                                                label={intl("rankingPropertyNo")}
                                                size="small"
                                                variant="filled"
                                                color="neutral"
                                                onClick={() =>
                                                    insert(property, { booleanValue: false })
                                                }
                                                sx={{ cursor: "pointer", fontSize: "0.7rem" }}
                                            />
                                        </>
                                    )}
                                    {samples.map((sample) => (
                                        <Chip
                                            key={`${property.name}-${sample}`}
                                            label={sample}
                                            size="small"
                                            variant="outlined"
                                            color="neutral"
                                            onClick={() =>
                                                insert(property, { mode: "sample", sample })
                                            }
                                            sx={{
                                                cursor: "pointer",
                                                fontSize: "0.65rem",
                                                maxWidth: "100%",
                                            }}
                                        />
                                    ))}
                                </Box>
                            )}
                            {isNumeric &&
                                filterDraft?.propertyName === property.name && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            alignItems: "center",
                                            gap: 0.5,
                                            mt: 0.5,
                                            pl: 0.25,
                                            width: "100%",
                                        }}
                                    >
                                        <TextInput
                                            autoComplete="off"
                                            size="small"
                                            fullWidth={false}
                                            placeholder={intl(
                                                "rankingPropertyFilterValuePlaceholder"
                                            )}
                                            value={filterDraft.value}
                                            onChange={(e) => {
                                                setFilterDraft({
                                                    ...filterDraft,
                                                    value: e.target.value,
                                                })
                                                setFilterError(null)
                                            }}
                                            onKeyDown={(e) => handleFilterKeyDown(e, property)}
                                            sx={{ flex: "1 1 100px", minWidth: 80, maxWidth: 160 }}
                                            inputProps={{ inputMode: "decimal" }}
                                        />
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            onClick={() => applyNumericFilter(property)}
                                        >
                                            {intl("rankingPropertyFilterApply")}
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="text"
                                            color="primary"
                                            onClick={() => {
                                                setFilterDraft(null)
                                                setFilterError(null)
                                            }}
                                        >
                                            {intl("rankingPropertyFilterCancel")}
                                        </Button>
                                        {filterError && (
                                            <Typography
                                                variant="caption"
                                                sx={{ color: "error.main", width: "100%" }}
                                            >
                                                {filterError}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            {isModal && hintSummary && (
                                <Typography
                                    variant="caption"
                                    color="secondary"
                                    sx={{ display: "block", mt: 0.35, pl: 0.25 }}
                                >
                                    {formatPropertyTypeLabel(property.type)}
                                    {hintSummary ? ` · ${hintSummary}` : ""}
                                </Typography>
                            )}
                        </Box>
                    )
                })}
            </Box>
        </Box>
    )
}

export { RankingPropertiesPicker, propertiesCache as rankingPropertiesCache }
